/* =====================================================================
 * Apple-like programmatic scroll
 *
 * Uses the classic iOS/PastryKit deceleration curve
 * cubic-bezier(.25, .46, .1, .94), a distance-aware duration, and
 * cancels immediately if the user starts scrolling.
 * Respects prefers-reduced-motion by jumping without animation.
 * ===================================================================== */

type Easing = [number, number, number, number];

const APPLE_DECELERATION: Easing = [0.25, 0.46, 0.1, 0.94];

const bezierValue = (t: number, p1: number, p2: number): number => {
  const c = 3 * p1;
  const b = 3 * (p2 - p1) - c;
  const a = 1 - c - b;
  return ((a * t + b) * t + c) * t;
};

const bezierSlope = (t: number, p1: number, p2: number): number => {
  const c = 3 * p1;
  const b = 3 * (p2 - p1) - c;
  const a = 1 - c - b;
  return (3 * a * t + 2 * b) * t + c;
};

const solveBezierT = (x: number, x1: number, x2: number): number => {
  let t = x;
  for (let i = 0; i < 6; i += 1) {
    const error = bezierValue(t, x1, x2) - x;
    const slope = bezierSlope(t, x1, x2);
    if (Math.abs(error) < 1e-5 || Math.abs(slope) < 1e-6) break;
    t -= error / slope;
  }
  return Math.min(1, Math.max(0, t));
};

const easeApple = (t: number): number => {
  const [x1, y1, x2, y2] = APPLE_DECELERATION;
  return bezierValue(solveBezierT(t, x1, x2), y1, y2);
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let cancelActiveScroll: (() => void) | null = null;

export interface SmoothScrollOptions {
  /** Space to leave above the target (defaults to the element's scroll-margin-top). */
  offset?: number;
  /** Align the element's top or bottom with the viewport. */
  align?: 'start' | 'end';
  /** Extra breathing room for align: end. */
  padding?: number;
  /** Override the distance-aware duration. */
  duration?: number;
  onDone?: () => void;
}

export function smoothScrollToElement(
  el: Element,
  options: SmoothScrollOptions = {}
): () => void {
  if (typeof window === 'undefined') return () => {};

  // A new navigation/scroll wins over the previous animation.
  cancelActiveScroll?.();

  const { align = 'start', padding = 20, duration, onDone } = options;
  const viewport = window.innerHeight;
  const docHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight
  );
  const maxY = Math.max(0, docHeight - viewport);
  const rect = el.getBoundingClientRect();
  const margin =
    options.offset ??
    (Number.parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0);

  const targetY =
    align === 'end'
      ? rect.bottom + window.scrollY - viewport + padding
      : rect.top + window.scrollY - margin;
  const clampedTarget = Math.min(maxY, Math.max(0, targetY));
  const startY = window.scrollY;
  const distance = Math.abs(clampedTarget - startY);

  if (distance < 2 || prefersReducedMotion()) {
    window.scrollTo(0, clampedTarget);
    onDone?.();
    return () => {};
  }

  const travel = duration ?? Math.min(900, Math.max(420, distance * 0.32));
  const startedAt = performance.now();
  let raf = 0;
  let cancelled = false;

  const cleanup = () => {
    window.removeEventListener('wheel', cancel);
    window.removeEventListener('touchstart', cancel);
    window.removeEventListener('keydown', cancel);
    if (cancelActiveScroll === cancel) cancelActiveScroll = null;
  };

  function cancel() {
    if (cancelled) return;
    cancelled = true;
    cancelAnimationFrame(raf);
    cleanup();
  }

  const tick = (now: number) => {
    if (cancelled) return;
    const progress = Math.min(1, (now - startedAt) / travel);
    const y = startY + (clampedTarget - startY) * easeApple(progress);
    window.scrollTo(0, y);
    if (progress < 1) {
      raf = requestAnimationFrame(tick);
    } else {
      cleanup();
      onDone?.();
    }
  };

  window.addEventListener('wheel', cancel, { passive: true });
  window.addEventListener('touchstart', cancel, { passive: true });
  window.addEventListener('keydown', cancel);
  cancelActiveScroll = cancel;
  raf = requestAnimationFrame(tick);

  return cancel;
}
